import { useSelector } from 'react-redux';
import { useDecision } from '@optimizely/react-sdk';
import { useRouter } from 'next/router';
import {
  PizzaIngredientOption
} from '@/builders/pizza/dataTransformers/builderTypes';
import { dealSelectors } from '@/builders/deals/slice/deal.slice';
import usePizzaSizeHardcoding from '../../../../../../hardcoding/utils/usePizzaSizeHardcoding';
import { useCCGetDealBuilderQuery } from '@/clientCore/temporaryTransformationalHooks/useCCGetDealBuilderQuery';
import { selectors } from '@/builders/pizza/slice/pizza.slice';
import { getRecipeUpchargeAmount, getSizeUpchargeAmount } from '@/clientCore/menu/utils/pizzaPrices';
import Routes from '@/router/routes';

const useDealBuilderSizePricing = (sizes: PizzaIngredientOption[], { skip = false }: { skip?: boolean} = {}): PizzaIngredientOption[] | undefined => {
  const deal = useSelector(dealSelectors.selectDeal);

  const pizzaId = useSelector(selectors.selectPizzaId);

  const { loading, rawData } = useCCGetDealBuilderQuery({ itemId: deal?.data?.id, skip: (!deal || skip) });

  if (loading || skip) {
    return undefined;
  }

  const selectedRecipe = rawData?.steps?.[0]?.recipes?.find((recipe) => recipe.id === pizzaId);
  const rawSizes = selectedRecipe?.options?.find((option) => option.id.includes('size'))?.modifiers;
  const newSizes: PizzaIngredientOption[] | undefined = rawSizes?.reduce((acc: PizzaIngredientOption[], rawSize) => {
    const sizeUpcharge = selectedRecipe?.options ? getSizeUpchargeAmount(rawSize, selectedRecipe?.options) : 0;
    const recipeUpcharage = selectedRecipe?.options ? getRecipeUpchargeAmount(selectedRecipe?.options) : 0;
    const price = rawData?.price ? rawData.price + sizeUpcharge + recipeUpcharage : 0;
    const size = sizes.find((transformedSize) => transformedSize.id === rawSize.id);
    if (size) {
      const updatedSize: PizzaIngredientOption = {
        ...size,
        price,
        sizeUpcharge
      };
      acc.push(updatedSize);
    }
    return acc;
  }, []);

  return newSizes;
};

const useSizePricing = (sizes: PizzaIngredientOption[]): { shouldShowSizePrice: boolean; sizes: PizzaIngredientOption[]} => {
  const [{ enabled: isSizeDisplayOrderFlagEnabled }] = useDecision('fr-web-3546-pb-sizes-display-order');

  const router = useRouter();
  const isDealsFlow = router.asPath?.includes(Routes.DEALS);

  const { shouldShowSizePrice: shouldShowPriceHardcoding } = usePizzaSizeHardcoding();
  const dealSizes = useDealBuilderSizePricing(sizes, { skip: !isDealsFlow });

  const shouldShowPrice = shouldShowPriceHardcoding || !!dealSizes;
  const newSizes = dealSizes || sizes;
  const sortedSizes = [...newSizes]?.sort((a, b) => (b?.priority ?? 0) - (a?.priority ?? 0));
  return {
    shouldShowSizePrice: shouldShowPrice,
    sizes: isSizeDisplayOrderFlagEnabled ? sortedSizes : newSizes
  };
};

export { useSizePricing };
